import {Bar, BarChart, CartesianGrid, XAxis, YAxis} from 'recharts';
import {BarChart3, ChevronRight, RefreshCw} from 'lucide-react';
import {useMemo, useState} from 'react';
import {
  Button,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type ChartConfig
} from '@kroll-resolver-crisp/ui-components-react';
import type {ScrapyCollectionDailyStat} from '@/api/scrapyCollections';
import {useScrapyCollectionDailyStats} from '@/api/hooks';
import {cn} from '@/lib/utils';

/**
 Validated for colour-vision-deficiency separation as an ordered set (see the tokens in index.css),
 so slots are assigned in order and never re-stepped individually.
 */
const SERIES_COLORS = [
  'var(--chart-cat-1)',
  'var(--chart-cat-2)',
  'var(--chart-cat-3)',
  'var(--chart-cat-4)',
  'var(--chart-cat-5)',
  'var(--chart-cat-6)',
  'var(--chart-cat-7)',
  'var(--chart-cat-8)'
];

/**
 Colour comes from the series' slot in what is actually drawn, not its rank in the whole window.
 Keying off the window's full series list looks more stable but cycles: with fourteen collections
 the survivors of the roll-up sit at arbitrary indices, and any two of them eight apart paint
 identically - the exact confusion the roll-up exists to prevent. The trade is that narrowing the
 chart can recolour the series that survive; a duplicate hue among visible segments is the worse
 failure, because the legend then cannot name what you are looking at.
 */
function colorForSlot(name: string, index: number): string {
  if (name === OTHER_SERIES) {
    return OTHER_COLOR;
  }

  // Ponytail: only "show all" can exceed the palette, and cycling there is the cost of the
  // escape hatch. The default view is capped at TOP_SERIES, so it never reaches the modulo.
  return SERIES_COLORS[index % SERIES_COLORS.length]!;
}

const OTHER_SERIES = 'Other';
const OTHER_COLOR = 'var(--chart-cat-other)';

/** Past 8 slots the roll-up is what keeps the legend readable, so only "show all" ever cycles. */
const TOP_SERIES = 8;

const DAY_WINDOWS = [7, 30, 90] as const;

/** Keeps a busy day's tooltip shorter than the plot it sits on top of. */
const TOOLTIP_ROWS = 12;

export type ChartGroupBy = 'name' | 'affiliate' | 'policyViolation' | 'language' | 'query';

const GROUP_BY_OPTIONS: Array<{id: ChartGroupBy; label: string}> = [
  {id: 'name', label: 'Collection'},
  {id: 'affiliate', label: 'Customer'},
  {id: 'policyViolation', label: 'Policy Violation'},
  {id: 'language', label: 'Language'},
  {id: 'query', label: 'Query'}
];

/**
 Series values live under a namespaced key on each row so a series can never collide with the
 row's own fields. Group by Query with someone searching for "date" and an unprefixed key would
 overwrite the x-axis label with a number for every bar on the chart.
 */
export function seriesDataKey(name: string): string {
  return `s:${name}`;
}

const UNSPECIFIED = 'Unspecified';

function seriesKeyFor(row: ScrapyCollectionDailyStat, groupBy: ChartGroupBy): string {
  if (groupBy === 'affiliate') {
    return row.affiliate ?? UNSPECIFIED;
  }

  if (groupBy === 'policyViolation') {
    return row.policy_violation ?? UNSPECIFIED;
  }

  if (groupBy === 'language') {
    return row.language ?? UNSPECIFIED;
  }

  if (groupBy === 'query') {
    // The search_term column is non-nullable and falls back to "" for rows predating the
    // spider_args.query requirement.
    return row.query || UNSPECIFIED;
  }

  return row.name;
}

/**
 `date` is a DateOnly ("2026-08-26"). `new Date(iso)` would read that as UTC midnight and then
 render it in the viewer's zone, which shows the previous day anywhere west of Greenwich - so the
 parts are passed to the local-time constructor instead.
 */
function formatDayLabel(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year!, month! - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}

type StackTooltipEntry = {readonly name?: string; readonly value?: number; readonly color?: string};

/**
 A stacked bar's own total is the number analysts actually read off this chart, and Recharts has no
 built-in for it - the zero-height marker bar that used to carry a LabelList stopped rendering under
 Recharts 3, because a zero-value bar produces no rectangle for the label to anchor to.
 */
function StackTooltip({
  label,
  payload
}: {
  readonly label: string;
  readonly payload: readonly StackTooltipEntry[];
}) {
  const entries = payload
    .filter((p) => (p.value ?? 0) > 0)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  const total = entries.reduce((sum, p) => sum + (p.value ?? 0), 0);
  // Under "show all" a busy day can carry thirty series - a tooltip taller than the plot it
  // describes helps nobody, so the tail is counted rather than listed. The total still sums
  // every series.
  const shown = entries.slice(0, TOOLTIP_ROWS);

  return (
    <div className="bg-popover text-popover-foreground ring-foreground/10 w-64 p-2 text-xs ring-1">
      <p className="text-foreground mb-1 font-medium">{label}</p>
      {shown.map((entry) => (
        <p key={entry.name} className="flex items-center gap-1.5">
          <span aria-hidden className="h-2 w-2 shrink-0" style={{background: entry.color}} />
          {/* min-w-0 is what lets truncate actually shrink the name inside a flex row. */}
          <span className="text-muted-foreground min-w-0 truncate">{entry.name}</span>
          <span className="text-foreground ml-auto font-medium whitespace-nowrap">
            {entry.value}
          </span>
        </p>
      ))}
      {entries.length > shown.length ? (
        <p className="text-muted-foreground pl-3.5 italic">+{entries.length - shown.length} more</p>
      ) : null}
      <p className="border-border text-foreground mt-1 flex gap-4 border-t pt-1 font-semibold">
        <span>Total</span>
        <span className="ml-auto">{total}</span>
      </p>
    </div>
  );
}

/** One level of drill-down: a dimension pinned to a single value. */
export type DrillStep = {readonly dim: ChartGroupBy; readonly value: string};

/** Every narrowing the chart applies: the page's filter row, then each pinned drill step. */
function filterRows(
  allRows: readonly ScrapyCollectionDailyStat[],
  options: RowFilters
): readonly ScrapyCollectionDailyStat[] {
  const needle = options.searchQuery.trim().toLowerCase();
  return allRows.filter(
    (r) =>
      (options.affiliateFilter === 'all' || r.affiliate === options.affiliateFilter) &&
      (options.verticalFilter === 'all' || r.policy_violation === options.verticalFilter) &&
      (options.languageFilter === 'all' || r.language === options.languageFilter) &&
      (!needle ||
        r.name.toLowerCase().includes(needle) ||
        r.query.toLowerCase().includes(needle)) &&
      options.drill.every((step) => seriesKeyFor(r, step.dim) === step.value)
  );
}

/**
 Where to point the group-by after a drill: the finest dimension that is not already pinned and
 still splits what is on screen more than one way. Without this, drilling lands on a single-series
 chart and the next click has nothing to bite on - and it has to narrow by the page's filter row
 too, or it can pick a dimension that only splits rows the filters have already removed.
 */
export function nextGroupBy(
  allRows: readonly ScrapyCollectionDailyStat[],
  filters: RowFilters,
  current: ChartGroupBy
): ChartGroupBy {
  const pinned = new Set(filters.drill.map((step) => step.dim));
  const rows = filterRows(allRows, filters);
  // Finest grain first - a drill is a request to go deeper, not sideways.
  const candidates: ChartGroupBy[] = ['query', 'name', 'policyViolation', 'language', 'affiliate'];
  const open = candidates.filter((id) => !pinned.has(id));
  return (
    open.find((id) => new Set(rows.map((r) => seriesKeyFor(r, id))).size > 1) ?? open[0] ?? current
  );
}

export type RowFilters = {
  /** Pinned dimensions, outermost first. Every step narrows what the chart is built from. */
  readonly drill: readonly DrillStep[];
  readonly affiliateFilter: string;
  readonly verticalFilter: string;
  readonly languageFilter: string;
  readonly searchQuery: string;
};

export type ChartOptions = RowFilters & {
  readonly groupBy: ChartGroupBy;
  readonly showAll: boolean;
};

/**
 Filter, drill, group, roll up and pivot in one pass — everything between the single daily-stats
 response and what Recharts is handed. Pure, so switching group-by or drilling in never refetches.
 */
export function buildChart(allRows: readonly ScrapyCollectionDailyStat[], options: ChartOptions) {
  const {groupBy, showAll} = options;
  const rows = filterRows(allRows, options);

  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = seriesKeyFor(row, groupBy);
    totals.set(key, (totals.get(key) ?? 0) + row.item_count);
  }

  // Rank only decides which series survive the roll-up; it never decides their colour.
  const ranked = [...totals.entries()].sort(([, a], [, b]) => b - a).map(([name]) => name);
  const kept = showAll ? ranked : ranked.slice(0, TOP_SERIES);
  const keptSet = new Set(kept);
  const rolledUpCount = ranked.length - kept.length;

  const byDay = new Map<string, Record<string, number>>();
  for (const row of rows) {
    const key = seriesKeyFor(row, groupBy);
    const name = keptSet.has(key) ? key : OTHER_SERIES;
    const bucket = byDay.get(row.date) ?? {};
    bucket[seriesDataKey(name)] = (bucket[seriesDataKey(name)] ?? 0) + row.item_count;
    byDay.set(row.date, bucket);
  }

  // Alphabetical stack order, so a day's segments sit in the same order as the legend and don't
  // reshuffle as volumes move. "Other" always sits on top - unless a real series is already
  // called that, in which case the roll-up merges into it rather than being listed twice.
  const series = [...kept].sort((a, b) => a.localeCompare(b));
  if (rolledUpCount > 0 && !keptSet.has(OTHER_SERIES)) {
    series.push(OTHER_SERIES);
  }

  const chartData = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, counts]) => ({date: formatDayLabel(date), ...counts}));

  return {chartData, series, rolledUpCount};
}

type Props = {
  readonly affiliateFilter: string;
  readonly verticalFilter: string;
  readonly languageFilter: string;
  readonly searchQuery: string;
};

export function ItemsOverviewChart({
  affiliateFilter,
  verticalFilter,
  languageFilter,
  searchQuery
}: Props) {
  const [collapsed, setCollapsed] = useState(true);
  const [groupBy, setGroupBy] = useState<ChartGroupBy>('name');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [drill, setDrill] = useState<readonly DrillStep[]>([]);
  const [days, setDays] = useState<number>(30);
  const [showAll, setShowAll] = useState(false);

  const {data, isLoading, isFetching, refetch} = useScrapyCollectionDailyStats(days);
  const allRows = useMemo(() => data ?? [], [data]);

  const filters: RowFilters = useMemo(
    () => ({drill, affiliateFilter, verticalFilter, languageFilter, searchQuery}),
    [drill, affiliateFilter, verticalFilter, languageFilter, searchQuery]
  );

  const {chartData, series, rolledUpCount} = useMemo(
    () => buildChart(allRows, {...filters, groupBy, showAll}),
    [allRows, filters, groupBy, showAll]
  );

  // Drilling on a dimension that is already pinned would only ever re-select the one value
  // showing, so the only thing that blocks a click is the roll-up bucket.
  const canDrill = !drill.some((step) => step.dim === groupBy);

  function drillInto(value: string) {
    if (!canDrill || value === OTHER_SERIES) {
      return;
    }

    const next = [...drill, {dim: groupBy, value}];
    setDrill(next);
    setGroupBy(nextGroupBy(allRows, {...filters, drill: next}, groupBy));
    setHidden(new Set());
  }

  function removeDrillStep(index: number) {
    setDrill(drill.filter((_, i) => i !== index));
    setHidden(new Set());
  }

  // The series slot (not the name) keys the chart config: names are user data and can carry
  // characters a CSS custom property name cannot, and the slot is what the colour is keyed on
  // anyway (see colorForSlot).
  const chartConfig: ChartConfig = Object.fromEntries(
    series.map((name, index) => [`s${index}`, {label: name, color: colorForSlot(name, index)}])
  );

  // A bordered panel on the page background rather than a Card: the chart's controls are form
  // controls, and its empty state is an Empty, neither of which sits on a card surface.
  return (
    <Collapsible
      open={!collapsed}
      className="border-border bg-card gap-0 rounded-lg border"
      onOpenChange={(open) => {
        setCollapsed(!open);
      }}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <CollapsibleTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2.5 text-sm font-medium"
            />
          }>
          <ChevronRight
            aria-hidden
            className="text-muted-foreground transition-transform in-data-panel-open:rotate-90"
          />
          Number of items per day
        </CollapsibleTrigger>
        <div className="flex items-center gap-3">
          {collapsed ? null : (
            <>
              <ToggleGroup
                variant="outline"
                spacing={0}
                size="sm"
                aria-label="Group by"
                value={[groupBy]}
                onValueChange={(next) => {
                  const [value] = next as ChartGroupBy[];
                  if (value) {
                    // Regroup within the current drill rather than abandoning it.
                    setGroupBy(value);
                    setHidden(new Set());
                  }
                }}>
                {GROUP_BY_OPTIONS.map((opt) => (
                  <ToggleGroupItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <ToggleGroup
                variant="outline"
                spacing={0}
                size="sm"
                aria-label="Window"
                value={[String(days)]}
                onValueChange={(next) => {
                  const [value] = next;
                  if (value) {
                    setDays(Number(value));
                  }
                }}>
                {DAY_WINDOWS.map((window) => (
                  <ToggleGroupItem key={window} value={String(window)}>
                    {window}d
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </>
          )}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  disabled={isFetching}
                  aria-label="Refresh"
                  onClick={() => {
                    void refetch();
                  }}
                />
              }>
              {isFetching ? <Spinner /> : <RefreshCw aria-hidden />}
            </TooltipTrigger>
            <TooltipContent>Refresh</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <CollapsibleContent>
        <div className="space-y-4 px-4 pb-4">
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            {drill.map((step, index) => (
              <Tooltip key={`${step.dim}:${step.value}`}>
                <TooltipTrigger
                  render={
                    <Button
                      type="button"
                      variant="secondary"
                      size="xs"
                      className="max-w-[16rem]"
                      onClick={() => {
                        removeDrillStep(index);
                      }}
                    />
                  }>
                  <span className="truncate">
                    {GROUP_BY_OPTIONS.find((o) => o.id === step.dim)?.label ?? step.dim}:{' '}
                    {step.value}
                  </span>
                  <span aria-hidden="true" className="shrink-0">
                    ×
                  </span>
                </TooltipTrigger>
                <TooltipContent>Remove this filter</TooltipContent>
              </Tooltip>
            ))}
            {drill.length > 1 ? (
              <Button
                type="button"
                variant="link"
                size="xs"
                onClick={() => {
                  setDrill([]);
                  setHidden(new Set());
                }}>
                Clear all
              </Button>
            ) : null}
            {rolledUpCount > 0 || showAll ? (
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => {
                  setShowAll((previous) => !previous);
                }}>
                {showAll
                  ? `Show top ${TOP_SERIES}`
                  : `Show all (${series.length + rolledUpCount - 1})`}
              </Button>
            ) : null}
            <span>
              Click a name below to show/hide it
              {canDrill ? '; click a bar segment to drill into it' : ''}
            </span>
          </div>

          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-10 text-xs">
              <Spinner aria-label="Loading run history" /> Loading run history…
            </div>
          ) : chartData.length === 0 ? (
            <Empty className="py-6">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <BarChart3 aria-hidden />
                </EmptyMedia>
                <EmptyTitle>No items collected</EmptyTitle>
                <EmptyDescription>
                  No items collected in the last {days} days for this filter.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <ChartContainer config={chartConfig} className="aspect-auto h-[320px] w-full pt-2">
              <BarChart data={chartData} margin={{top: 24, right: 16, bottom: 8, left: 0}}>
                <CartesianGrid
                  vertical={false}
                  stroke="var(--color-border)"
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="date"
                  tick={{fontSize: 10, fill: 'var(--color-muted-foreground)'}}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis
                  tick={{fontSize: 10, fill: 'var(--color-muted-foreground)'}}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={30}
                />
                <ChartTooltip
                  cursor={{fill: 'var(--color-muted)'}}
                  // Recharts emits the legend wrapper after the tooltip wrapper, so by DOM order the
                  // legend paints over the tooltip. pointerEvents keeps it from eating legend clicks.
                  wrapperStyle={{zIndex: 50, pointerEvents: 'none'}}
                  content={({active, label, payload}) =>
                    active && payload?.length ? (
                      <StackTooltip
                        label={String(label)}
                        // Filtered here rather than trusting Recharts to drop a `hide`-n series from
                        // the payload, so the total always matches what is actually drawn.
                        payload={(payload as readonly StackTooltipEntry[]).filter(
                          (entry) => !hidden.has(entry.name ?? '')
                        )}
                      />
                    ) : null
                  }
                />
                {/* The package's ChartLegendContent has no click handling, and show/hide by legend
                    click is this chart's whole interaction - so the legend keeps its own formatter. */}
                <ChartLegend
                  formatter={(value) => (
                    <span
                      className={cn(
                        'cursor-pointer text-xs font-medium',
                        hidden.has(value) ? 'text-muted-foreground line-through' : 'text-foreground'
                      )}>
                      {value}
                    </span>
                  )}
                  onClick={(entry) => {
                    const name = entry.value!;
                    setHidden((previous) => {
                      const next = new Set(previous);
                      if (next.has(name)) {
                        next.delete(name);
                      } else {
                        next.add(name);
                      }

                      return next;
                    });
                  }}
                />
                {series.map((name, index) => (
                  <Bar
                    key={name}
                    dataKey={seriesDataKey(name)}
                    name={name}
                    stackId="items"
                    hide={hidden.has(name)}
                    fill={`var(--color-s${index})`}
                    // Painted in the surface colour so adjacent segments in a stack read as separated
                    // rather than as one continuous block.
                    stroke="var(--color-card)"
                    strokeWidth={2}
                    maxBarSize={48}
                    cursor={canDrill && name !== OTHER_SERIES ? 'pointer' : undefined}
                    onClick={() => {
                      drillInto(name);
                    }}
                  />
                ))}
              </BarChart>
            </ChartContainer>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
